import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

export const SortableItem = ({
  id,
  media,
  index,
  selectedMediaIndex,
  setSelectedMediaIndex,
  handleDeleteMedia,
  theme,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: id || `fallback-id-${index}`,
    data: { index },
  });

  // Handler for image selection
  const handleImageSelect = (e) => {
    e.stopPropagation();
    setSelectedMediaIndex(index);
  };

  // Handler for delete
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    handleDeleteMedia(index);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 9999 : 1,
    marginRight: 8,
    flexShrink: 0,
    position: "relative",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {/* The image container with visual drag handle */}
      <Box
        sx={{
          position: "relative",
          width: 80,
          height: 90, // Slightly taller to accommodate drag handle
          cursor: isDragging ? "grabbing" : "grab",
        }}>
        {/* The actual image/preview box */}
        <Box
          onClick={handleImageSelect}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "calc(100% - 16px)", // Leave space at bottom for drag handle
            borderRadius: "4px 4px 0 0", // Rounded corners at top
            overflow: "hidden",
            border:
              selectedMediaIndex === index
                ? `3px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.divider}`,
            borderBottom: 0, // No border at bottom
            backgroundColor: theme.palette.background.default,
            boxShadow: isDragging ? "0px 5px 10px rgba(0,0,0,0.2)" : "none",
            cursor: "pointer",
          }}>
          {media.type === "image" ? (
            <img
              src={media.preview}
              alt={`Media ${index + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: theme.palette.background.gray,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Typography variant="caption">Video</Typography>
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              textAlign: "center",
              p: 0.5,
            }}>
            {index + 1}
          </Typography>
        </Box>

        {/* Drag handle at the bottom */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "16px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: theme.palette.background.paper,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            cursor: "grab",
            zIndex: 5,
            border: `1px solid ${theme.palette.divider}`,
            borderTop: 0,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}>
          <DragIndicatorIcon
            fontSize="small"
            sx={{ fontSize: 16, color: theme.palette.text.secondary }}
          />
        </Box>
      </Box>

      {/* Delete button */}
      {!isDragging && (
        <IconButton
          aria-label="delete"
          onClick={handleDeleteClick}
          size="small"
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "#fff",
            position: "absolute",
            top: -8,
            right: -8,
            width: 20,
            height: 20,
            minWidth: 20,
            padding: 0,
            zIndex: 10,
            "& .MuiSvgIcon-root": {
              fontSize: "0.75rem",
            },
            "&:hover": {
              backgroundColor: theme.palette.error.dark,
            },
          }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};
